import React, { useMemo, useState } from 'react';
import {
    Chip,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { useIntl } from 'react-intl';
import DisabledSourceChip from './DisabledSourceChip';
import SortableTableHeaderCell from './SortableTableHeaderCell';
import { applySortDirection, compareDates, compareNumbers, compareStrings } from './tableSort';
import { useNavigate, useParams } from 'react-router-dom';
import RoutePaths from '../routes/RoutePaths';

const STATUS_CONFIG = {
    healthy: { color: 'success', labelId: 'monitoring.status.healthy' },
    failures: { color: 'warning', labelId: 'monitoring.status.failures' },
    error: { color: 'error', labelId: 'monitoring.status.error' },
    never_run: { color: 'warning', labelId: 'monitoring.status.neverRun' },
};

const STATUS_ORDER = { error: 0, failures: 1, never_run: 2, healthy: 3 };

const GAP_COLUMNS = [
    {
        id: 'lastNew',
        type: 'datetime',
        key: 'last_new_at',
        wrap: true,
        headerMaxWidth: 88,
    },
    {
        id: 'silence',
        type: 'seconds',
        key: 'current_silence_seconds',
        wrap: true,
        headerMaxWidth: 80,
    },
    {
        id: 'p90Gap',
        type: 'seconds',
        key: 'p90_gap_seconds',
        tooltip: true,
        wrap: true,
        headerMaxWidth: 96,
    },
];

const NUMERIC_COLUMNS = [
    { id: 'itemsNew', key: 'items_new', wrap: true, headerMaxWidth: 96 },
    {
        id: 'evaluatorRejected',
        key: 'pipeline_evaluator_rejected',
        wrap: true,
        headerMaxWidth: 96,
    },
    {
        id: 'published',
        key: 'pipeline_publish_succeeded',
        highlightColor: 'success.main',
        wrap: true,
        headerMaxWidth: 96,
    },
    {
        id: 'creationFailed',
        keys: ['pipeline_publish_failed', 'pipeline_failed'],
        highlightColor: 'error.main',
        dividerBefore: true,
        wrap: true,
        headerMaxWidth: 104,
    },
    {
        id: 'runsFailed',
        key: 'runs_failed',
        highlightColor: 'error.main',
        wrap: true,
        headerMaxWidth: 96,
    },
];

const COLUMN_DIVIDER_SX = {
    borderLeft: '2px solid',
    borderLeftColor: 'divider',
};

const GAP_COLUMN_IDS = new Set(GAP_COLUMNS.map((column) => column.id));
const NUMERIC_COLUMN_IDS = new Set(NUMERIC_COLUMNS.map((column) => column.id));

const STICKY_SOURCE_BODY_SX = {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    bgcolor: 'background.paper',
    minWidth: 160,
    maxWidth: 240,
    boxShadow: '4px 0 8px -6px rgba(0, 0, 0, 0.18)',
    'tbody tr:hover &': {
        bgcolor: 'action.hover',
    },
};

const STICKY_SOURCE_HEADER_SX = {
    ...STICKY_SOURCE_BODY_SX,
    zIndex: 3,
};

const COMPACT_CELL_SX = {
    whiteSpace: 'nowrap',
    px: 1,
};

function StatusChip({ status }) {
    const intl = useIntl();
    const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.healthy;
    return (
        <Chip
            size="small"
            color={config.color}
            variant="outlined"
            label={intl.formatMessage({ id: config.labelId })}
        />
    );
}

function formatDateTime(value, locale) {
    if (!value) return '–';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '–';
    return date.toLocaleString(locale ?? 'fi-FI', {
        dateStyle: 'short',
        timeStyle: 'short',
    });
}

function formatDurationSeconds(seconds, locale) {
    if (typeof seconds !== 'number') {
        return '–';
    }
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const isFinnish = String(locale).toLowerCase().startsWith('fi');
    const dayLabel = isFinnish ? 'pv' : 'd';

    if (days > 0 && hours > 0) {
        return `${days} ${dayLabel} ${hours} h`;
    }
    if (days > 0) {
        return `${days} ${dayLabel}`;
    }
    if (hours > 0) {
        return `${hours} h`;
    }
    if (minutes > 0) {
        return `${minutes} min`;
    }
    return `${seconds} s`;
}

function NumberCell({ value, highlightColor, sx = {} }) {
    const numeric = typeof value === 'number' ? value : null;
    const isHighlighted = highlightColor && numeric > 0;
    return (
        <TableCell align="right" sx={{ borderColor: 'divider', ...COMPACT_CELL_SX, ...sx }}>
            <Typography
                variant="body2"
                sx={{
                    fontWeight: isHighlighted ? 600 : 400,
                    color: isHighlighted ? highlightColor : 'text.primary',
                }}
            >
                {numeric ?? '–'}
            </Typography>
        </TableCell>
    );
}

function ErrorMessageCell({ value }) {
    const errorText = value ?? '';

    return (
        <TableCell sx={{ borderColor: 'divider', maxWidth: 240, overflow: 'hidden' }}>
            <Tooltip title={errorText} arrow disableHoverListener={!errorText}>
                <Typography
                    variant="body2"
                    color="error.main"
                    noWrap
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                    {errorText}
                </Typography>
            </Tooltip>
        </TableCell>
    );
}

function getGapValue(gapStats, column) {
    if (!gapStats) {
        return null;
    }
    return gapStats[column.key] ?? null;
}

function buildGapTooltip(gapStats, intl, locale) {
    if (!gapStats) {
        return '';
    }

    const parts = [];
    if (gapStats.stats_computed_at) {
        parts.push(
            intl.formatMessage(
                { id: 'monitoring.detail.gapStats.computedAt' },
                { value: formatDateTime(gapStats.stats_computed_at, locale) }
            )
        );
    }
    parts.push(
        intl.formatMessage(
            { id: 'monitoring.detail.gapStats.sampleCount' },
            { count: gapStats.gap_count ?? 0 }
        )
    );
    return parts.join(' ');
}

function GapCell({ column, gapStats, intl }) {
    const value = getGapValue(gapStats, column);
    const displayValue =
        column.type === 'datetime'
            ? formatDateTime(value, intl.locale)
            : formatDurationSeconds(value, intl.locale);
    const tooltipParts = [];
    if (column.tooltip) {
        tooltipParts.push(buildGapTooltip(gapStats, intl, intl.locale));
    }
    const tooltip = tooltipParts.filter(Boolean).join(' ');

    return (
        <TableCell align="right" sx={{ borderColor: 'divider', ...COMPACT_CELL_SX }}>
            <Tooltip title={tooltip} arrow disableHoverListener={!tooltip}>
                <Typography variant="body2" color="text.secondary" noWrap>
                    {displayValue}
                </Typography>
            </Tooltip>
        </TableCell>
    );
}

function getColumnValue(weekly, column) {
    if (!weekly) {
        return null;
    }
    if (column.keys) {
        let hasValue = false;
        let sum = 0;
        for (const key of column.keys) {
            const value = weekly[key];
            if (typeof value === 'number') {
                hasValue = true;
                sum += value;
            }
        }
        return hasValue ? sum : null;
    }
    const value = weekly[column.key];
    return typeof value === 'number' ? value : null;
}

function compareSourceRows(a, b, orderBy) {
    const weeklyA = a?.weekly_summary;
    const weeklyB = b?.weekly_summary;
    const gapA = a?.gap_stats;
    const gapB = b?.gap_stats;

    if (orderBy === 'source') {
        return compareStrings(a?.source_id, b?.source_id);
    }
    if (orderBy === 'status') {
        return compareNumbers(STATUS_ORDER[a?.status] ?? 99, STATUS_ORDER[b?.status] ?? 99);
    }
    if (orderBy === 'lastRun') {
        return compareDates(weeklyA?.last_run_at, weeklyB?.last_run_at);
    }
    if (GAP_COLUMN_IDS.has(orderBy)) {
        const column = GAP_COLUMNS.find((entry) => entry.id === orderBy);
        const valueA = getGapValue(gapA, column);
        const valueB = getGapValue(gapB, column);
        if (column.type === 'datetime') {
            return compareDates(valueA, valueB);
        }
        return compareNumbers(valueA, valueB);
    }
    if (orderBy === 'error') {
        return compareStrings(a?.error_summary, b?.error_summary);
    }
    if (NUMERIC_COLUMN_IDS.has(orderBy)) {
        const column = NUMERIC_COLUMNS.find((entry) => entry.id === orderBy);
        return compareNumbers(getColumnValue(weeklyA, column), getColumnValue(weeklyB, column));
    }
    return compareStrings(a?.source_id, b?.source_id);
}

function getSourceDetailPath(mediaId, sourceId) {
    return RoutePaths.monitoring.source
        .replace(':mediaId', encodeURIComponent(mediaId))
        .replace(':sourceId', encodeURIComponent(sourceId));
}

export default function SourceSummaryTable({ sources, mediaId: mediaIdProp }) {
    const intl = useIntl();
    const navigate = useNavigate();
    const { mediaId: mediaIdParam } = useParams();
    const mediaId = mediaIdProp ?? mediaIdParam;
    const [orderBy, setOrderBy] = useState('status');
    const [order, setOrder] = useState('asc');

    const handleSort = (columnId) => {
        const isAsc = orderBy === columnId && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(columnId);
    };

    const sortedSources = useMemo(() => {
        return [...(sources ?? [])].sort((a, b) => {
            const result = compareSourceRows(a, b, orderBy);
            if (result !== 0) {
                return applySortDirection(result, order);
            }
            return compareStrings(a?.source_id, b?.source_id);
        });
    }, [sources, order, orderBy]);

    const headerCellSx = {
        fontWeight: 600,
        color: 'text.secondary',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        fontSize: '0.75rem',
        whiteSpace: 'normal',
        lineHeight: 1.25,
        verticalAlign: 'bottom',
        px: 1,
    };

    const columnLabel = (id) =>
        intl.formatMessage({ id: `monitoring.detail.column.${id}` });

    return (
        <TableContainer
            sx={{
                height: '100%',
                width: '100%',
                overflow: 'auto',
            }}
        >
            <Table
                stickyHeader
                size="small"
                sx={{ minWidth: 1280 }}
                aria-label={intl.formatMessage({ id: 'monitoring.detail.title' })}
            >
                <TableHead>
                    <TableRow>
                        <SortableTableHeaderCell
                            columnId="source"
                            label={columnLabel('source')}
                            orderBy={orderBy}
                            order={order}
                            onSort={handleSort}
                            sx={{ ...headerCellSx, ...STICKY_SOURCE_HEADER_SX }}
                        />
                        <SortableTableHeaderCell
                            columnId="status"
                            label={columnLabel('status')}
                            orderBy={orderBy}
                            order={order}
                            onSort={handleSort}
                            sx={headerCellSx}
                        />
                        <SortableTableHeaderCell
                            columnId="lastRun"
                            label={columnLabel('lastRun')}
                            wrap
                            orderBy={orderBy}
                            order={order}
                            onSort={handleSort}
                            sx={{ ...headerCellSx, ...COLUMN_DIVIDER_SX, maxWidth: 88 }}
                        />
                        {GAP_COLUMNS.map((column) => (
                            <SortableTableHeaderCell
                                key={column.id}
                                columnId={column.id}
                                label={columnLabel(column.id)}
                                wrap={column.wrap}
                                orderBy={orderBy}
                                order={order}
                                onSort={handleSort}
                                align="right"
                                sx={{
                                    ...headerCellSx,
                                    ...(column.headerMaxWidth
                                        ? { maxWidth: column.headerMaxWidth }
                                        : {}),
                                }}
                            />
                        ))}
                        {NUMERIC_COLUMNS.map((column) => (
                            <SortableTableHeaderCell
                                key={column.id}
                                columnId={column.id}
                                label={columnLabel(column.id)}
                                wrap={column.wrap}
                                orderBy={orderBy}
                                order={order}
                                onSort={handleSort}
                                align="right"
                                sx={{
                                    ...headerCellSx,
                                    ...(column.dividerBefore ? COLUMN_DIVIDER_SX : {}),
                                    ...(column.headerMaxWidth
                                        ? { maxWidth: column.headerMaxWidth }
                                        : {}),
                                }}
                            />
                        ))}
                        <SortableTableHeaderCell
                            columnId="error"
                            label={columnLabel('error')}
                            wrap
                            orderBy={orderBy}
                            order={order}
                            onSort={handleSort}
                            sx={{ ...headerCellSx, maxWidth: 88 }}
                        />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedSources.map((source) => {
                        const weekly = source.weekly_summary;
                        const gapStats = source.gap_stats;
                        return (
                            <TableRow
                                key={source.source_id}
                                hover
                                onClick={() => navigate(getSourceDetailPath(mediaId, source.source_id))}
                                sx={{ cursor: 'pointer' }}
                            >
                                <TableCell
                                    sx={{
                                        borderColor: 'divider',
                                        ...STICKY_SOURCE_BODY_SX,
                                        ...COMPACT_CELL_SX,
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 500 }}
                                            noWrap
                                        >
                                            {source.source_id}
                                        </Typography>
                                        {source.enabled === false && <DisabledSourceChip />}
                                    </Stack>
                                </TableCell>
                                <TableCell sx={{ borderColor: 'divider', ...COMPACT_CELL_SX }}>
                                    <StatusChip status={source.status} />
                                </TableCell>
                                <TableCell
                                    sx={{
                                        borderColor: 'divider',
                                        ...COLUMN_DIVIDER_SX,
                                        ...COMPACT_CELL_SX,
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {formatDateTime(weekly?.last_run_at, intl.locale)}
                                    </Typography>
                                </TableCell>
                                {GAP_COLUMNS.map((column) => (
                                    <GapCell
                                        key={column.id}
                                        column={column}
                                        gapStats={gapStats}
                                        intl={intl}
                                    />
                                ))}
                                {NUMERIC_COLUMNS.map((column) => (
                                    <NumberCell
                                        key={column.id}
                                        value={getColumnValue(weekly, column)}
                                        highlightColor={column.highlightColor}
                                        sx={column.dividerBefore ? COLUMN_DIVIDER_SX : undefined}
                                    />
                                ))}
                                <ErrorMessageCell value={source.error_summary} />
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
